// src/pages/DashboardHome.js
import React from "react";
import {
    Grid,
    Paper,
    Typography,
    CircularProgress,
    Alert,
    Box,
    Divider,
    Button,
} from "@mui/material";
import PageContainer from "../layout/PageContainer";
import axios from "axios";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

// English comment: Convert any value to a finite number (fallback 0)
function toFiniteNumber(v) {
    const n = typeof v === "number" ? v : Number(v);
    return Number.isFinite(n) ? n : 0;
}

function formatShortDate(value) {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return date.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" });
}

// English comment: Normalize Matomo visits series response to number[]
function normalizeVisitsSeries(data) {
    if (!data) return [];

    // Case A: expected object date -> number or date -> { nb_visits: number }
    if (typeof data === "object" && !Array.isArray(data)) {
        // Sometimes Matomo wraps errors/success
        if (data.result === "error") return [];
        const keys = Object.keys(data);
        // If it looks like a keyed-by-date object
        if (keys.length && keys[0].match(/^\d{4}-\d{2}-\d{2}$/)) {
            return keys
                .sort()
                .map((k) => {
                    const v = data[k];
                    if (v && typeof v === "object") {
                        // e.g. { nb_visits: 12 }
                        if ("nb_visits" in v) return toFiniteNumber(v.nb_visits);
                        if ("value" in v) return toFiniteNumber(v.value);
                    }
                    return toFiniteNumber(v);
                });
        }
    }

    // Case B: array of points like [{date, nb_visits}] or [{label, value}]
    if (Array.isArray(data)) {
        return data
            .map((row) => {
                if (!row || typeof row !== "object") return null;
                if ("nb_visits" in row) return toFiniteNumber(row.nb_visits);
                if ("value" in row) return toFiniteNumber(row.value);
                if ("visits" in row) return toFiniteNumber(row.visits);
                return null;
            })
            .filter((v) => v !== null);
    }

    return [];
}

// English comment: Normalize Matomo last visits response to array
function normalizeLastVisits(data) {
    if (!data) return [];

    if (Array.isArray(data)) return data;

    if (typeof data === "object") {
        if (data.result === "error") return [];
        // Some wrappers might return { visits: [...] }
        if (Array.isArray(data.visits)) return data.visits;
        if (Array.isArray(data.data)) return data.data;
    }

    return [];
}

function Sparkline({ points = [], labels = [], height = 46, ariaLabel = "Sparkline" }) {
    const safe = Array.isArray(points) ? points.map(toFiniteNumber) : [];
    if (!safe.length) return null;

    const w = 220;
    const h = height;

    const min = Math.min(...safe);
    const max = Math.max(...safe);
    const range = Math.max(1, max - min);

    const toX = (i) => (i / Math.max(1, safe.length - 1)) * (w - 4) + 2;
    const toY = (v) => h - ((v - min) / range) * (h - 4) - 2;

    const d = safe
        .map((v, i) => {
            const x = toX(i);
            const y = toY(v);
            if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
            return `${i === 0 ? "M" : "L"} ${x.toFixed(2)} ${y.toFixed(2)}`;
        })
        .filter(Boolean)
        .join(" ");

    if (!d) return null;

    return (
        <Box sx={{ width: "100%", overflow: "hidden" }}>
            <svg viewBox={`0 0 ${w} ${h}`} width="100%" height={h} aria-label={ariaLabel}>
                <path d={d} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <title>{ariaLabel}</title>
                </path>
                {safe.map((v, i) => (
                    <circle key={`${i}-${v}`} cx={toX(i)} cy={toY(v)} r="3" fill="currentColor">
                        <title>{`${labels[i] || `Tag ${i + 1}`}: ${v} Klicks`}</title>
                    </circle>
                ))}
            </svg>
        </Box>
    );
}

export default function DashboardHome() {
    const [activeCompaniesCount, setActiveCompaniesCount] = React.useState(null);
    const [loadingCount, setLoadingCount] = React.useState(false);
    const [countError, setCountError] = React.useState("");

    const [matomoError, setMatomoError] = React.useState("");
    const [matomoLoading, setMatomoLoading] = React.useState(false);
    const [lastVisits, setLastVisits] = React.useState([]);
    const [visitsSeries, setVisitsSeries] = React.useState([]);
    const [companyViewsLoading, setCompanyViewsLoading] = React.useState(false);
    const [companyViewsError, setCompanyViewsError] = React.useState("");
    const [companyViews, setCompanyViews] = React.useState({
        summary: {},
        top_pages: [],
        daily: [],
        by_category: [],
    });

    // English comment: Keep raw responses for debugging when parsing yields empty
    const [rawLast, setRawLast] = React.useState(null);
    const [rawSeries, setRawSeries] = React.useState(null);

    // English comment: Prevent double-fetch in React StrictMode (dev)
    const didFetchRef = React.useRef(false);

    const fetchActiveCompaniesCount = React.useCallback(async () => {
        setLoadingCount(true);
        setCountError("");
        try {
            const res = await axios.get(`${API_BASE}/api/dashboard/active-companies/count`, {
                withCredentials: true,
            });
            const count = Number(res?.data?.count);
            setActiveCompaniesCount(Number.isFinite(count) ? count : 0);
        } catch (e) {
            setCountError(e?.response?.data?.error || e.message || "Abfrage fehlgeschlagen.");
            setActiveCompaniesCount(null);
        } finally {
            setLoadingCount(false);
        }
    }, []);

    const fetchMatomoOnce = React.useCallback(async () => {
        setMatomoLoading(true);
        setMatomoError("");

        try {
            const [lastRes, seriesRes] = await Promise.all([
                axios.get(`${API_BASE}/api/matomo/last-visits?limit=8`, { withCredentials: true }),
                axios.get(`${API_BASE}/api/matomo/visits-series?days=30`, { withCredentials: true }),
            ]);

            setRawLast(lastRes.data);
            setRawSeries(seriesRes.data);

            const last = normalizeLastVisits(lastRes.data);
            const series = normalizeVisitsSeries(seriesRes.data);

            setLastVisits(last);
            setVisitsSeries(series);
        } catch (e) {
            const status = e?.response?.status;

            if (status === 429) {
                setMatomoError("Matomo ist temporär gesperrt (zu viele Fehlversuche). Bitte später erneut laden.");
            } else {
                setMatomoError(e?.response?.data?.error || e.message || "Matomo-Abfrage fehlgeschlagen.");
            }

            setLastVisits([]);
            setVisitsSeries([]);
        } finally {
            setMatomoLoading(false);
        }
    }, []);

    const fetchCompanyViews = React.useCallback(async () => {
        setCompanyViewsLoading(true);
        setCompanyViewsError("");
        try {
            const res = await axios.get(`${API_BASE}/api/dashboard/company-views?days=30&top_limit=20`, {
                withCredentials: true,
            });
            setCompanyViews({
                summary: res.data?.summary || {},
                top_pages: Array.isArray(res.data?.top_pages) ? res.data.top_pages : [],
                daily: Array.isArray(res.data?.daily) ? res.data.daily : [],
                by_category: Array.isArray(res.data?.by_category) ? res.data.by_category : [],
            });
        } catch (e) {
            setCompanyViewsError(e?.response?.data?.error || e.message || "Klickstatistiken konnten nicht geladen werden.");
        } finally {
            setCompanyViewsLoading(false);
        }
    }, []);

    React.useEffect(() => {
        fetchActiveCompaniesCount();

        // English comment: Only fetch once even in StrictMode dev (which mounts effects twice)
        if (didFetchRef.current) return;
        didFetchRef.current = true;

        fetchMatomoOnce();
        fetchCompanyViews();
    }, [fetchActiveCompaniesCount, fetchMatomoOnce, fetchCompanyViews]);

    const safeSeries = React.useMemo(
        () => (Array.isArray(visitsSeries) ? visitsSeries.map(toFiniteNumber) : []),
        [visitsSeries]
    );

    const showDebug = !matomoLoading && !matomoError && safeSeries.length === 0 && lastVisits.length === 0;
    const companyViewsDaily = React.useMemo(
        () => (companyViews.daily || []).map((row) => toFiniteNumber(row.views)),
        [companyViews.daily]
    );
    const companyViewsDailyLabels = React.useMemo(
        () => (companyViews.daily || []).map((row) => formatShortDate(row.date)),
        [companyViews.daily]
    );
    const companyViewsSummary = companyViews.summary || {};

    return (
        <PageContainer title="Übersicht" breadcrumbs={["Home", "Dashboard"]}>
            {countError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {countError}
                </Alert>
            )}

            {matomoError && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    {matomoError}
                </Alert>
            )}

            {companyViewsError && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    {companyViewsError}
                </Alert>
            )}

            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                <Box>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2 }}>
                        <Typography variant="h6" sx={{ fontWeight: 800 }}>
                            HRI Datenbank-Statistiken
                        </Typography>
                        <Button
                            variant="outlined"
                            onClick={() => {
                                fetchActiveCompaniesCount();
                                fetchCompanyViews();
                            }}
                            disabled={loadingCount || companyViewsLoading}
                        >
                            Datenbank-Statistiken neu laden
                        </Button>
                    </Box>

                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" },
                            gap: 3,
                            mt: 3,
                            alignItems: "stretch",
                            "& > .MuiBox-root": { minWidth: 0, display: "flex" },
                            "& > .MuiBox-root > .MuiPaper-root": { width: "100%", minHeight: 150 },
                        }}
                    >
                <Box>
                    <Paper sx={{ p: 3 }}>
                        <Typography variant="subtitle2" color="text.secondary">
                            Aktive Firmen
                        </Typography>

                        {loadingCount ? (
                            <Typography
                                variant="h3"
                                sx={{ mt: 0.5, display: "flex", alignItems: "center", gap: 1 }}
                            >
                                <CircularProgress size={28} />
                            </Typography>
                        ) : (
                            <Typography variant="h3" sx={{ mt: 0.5 }}>
                                {activeCompaniesCount ?? "-"}
                            </Typography>
                        )}
                    </Paper>
                </Box>

                <Box>
                    <Paper sx={{ p: 3 }}>
                        <Typography variant="subtitle2" color="text.secondary">
                            Neue Anmeldungen (30T)
                        </Typography>
                        <Typography variant="h3" sx={{ mt: 0.5 }}>
                            34
                        </Typography>
                    </Paper>
                </Box>

                <Box>
                    <Paper sx={{ p: 3 }}>
                        <Typography variant="subtitle2" color="text.secondary">
                            Systemstatus
                        </Typography>
                        <Typography variant="h3" sx={{ mt: 0.5 }}>
                            OK
                        </Typography>
                    </Paper>
                </Box>

                    </Box>

                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" },
                            gap: 3,
                            mt: 3,
                            mb: 6,
                            alignItems: "stretch",
                            "& > .MuiBox-root": { minWidth: 0, display: "flex" },
                            "& > .MuiBox-root > .MuiPaper-root": { width: "100%", minHeight: 430 },
                        }}
                    >
                <Box>
                    <Paper sx={{ p: 3, height: "100%" }}>
                        <Typography variant="subtitle2" color="text.secondary">
                            Firmenseiten – Klicks
                        </Typography>
                        {companyViewsLoading ? (
                            <Box sx={{ mt: 1 }}><CircularProgress size={24} /></Box>
                        ) : (
                            <>
                                <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2, mt: 1 }}>
                                    <Box>
                                        <Typography variant="h4">{toFiniteNumber(companyViewsSummary.today)}</Typography>
                                        <Typography variant="caption" color="text.secondary">Heute</Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="h4">{toFiniteNumber(companyViewsSummary.last_7_days)}</Typography>
                                        <Typography variant="caption" color="text.secondary">7 Tage</Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="h4">{toFiniteNumber(companyViewsSummary.last_30_days)}</Typography>
                                        <Typography variant="caption" color="text.secondary">30 Tage</Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="h4">{toFiniteNumber(companyViewsSummary.total)}</Typography>
                                        <Typography variant="caption" color="text.secondary">Gesamt</Typography>
                                    </Box>
                                </Box>
                                {companyViewsDaily.length > 0 && (
                                    <Box sx={{ mt: 2, color: "#f0ac4d" }}>
                                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                                            Verlauf Firmenseiten-Klicks, letzte 30 Tage
                                        </Typography>
                                        <Sparkline
                                            points={companyViewsDaily}
                                            labels={companyViewsDailyLabels}
                                            height={62}
                                            ariaLabel="Firmenseiten-Klicks letzte 30 Tage"
                                        />
                                    </Box>
                                )}
                            </>
                        )}
                    </Paper>
                </Box>

                <Box>
                    <Paper sx={{ p: 3, height: "100%" }}>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
                            Top 20 meist geklickte Firmenseiten
                        </Typography>
                        {companyViewsLoading ? (
                            <CircularProgress size={24} />
                        ) : companyViews.top_pages?.length ? (
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 1, maxHeight: 360, overflow: "auto", pr: 0.5 }}>
                                {companyViews.top_pages.map((row, index) => (
                                    <Box key={row.id} sx={{ display: "grid", gridTemplateColumns: "30px minmax(0, 1fr) 70px", gap: 1, alignItems: "center" }}>
                                        <Typography variant="body2" color="text.secondary">#{index + 1}</Typography>
                                        <Box sx={{ minWidth: 0 }}>
                                            <Typography variant="body2" sx={{ fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                {row.name || "-"}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {[row.city, row.country].filter(Boolean).join(", ") || "-"}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ textAlign: "right" }}>
                                            <Typography variant="body2" sx={{ fontWeight: 700 }}>{toFiniteNumber(row.views_total)}</Typography>
                                            <Typography variant="caption" color="text.secondary">{toFiniteNumber(row.views_30_days)} / 30T</Typography>
                                        </Box>
                                    </Box>
                                ))}
                            </Box>
                        ) : (
                            <Typography variant="body2" color="text.secondary">Keine Klickdaten.</Typography>
                        )}
                    </Paper>
                </Box>

                <Box>
                    <Paper sx={{ p: 3, height: "100%" }}>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
                            Klicks nach Kategorie (30T)
                        </Typography>
                        {companyViewsLoading ? (
                            <CircularProgress size={24} />
                        ) : companyViews.by_category?.length ? (
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
                                {companyViews.by_category.map((row) => {
                                    const max = Math.max(...companyViews.by_category.map((item) => toFiniteNumber(item.views)), 1);
                                    const pct = Math.max(4, Math.round((toFiniteNumber(row.views) / max) * 100));
                                    return (
                                        <Box key={row.id}>
                                            <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
                                                <Typography variant="body2" sx={{ fontWeight: 700 }}>{row.label_de || row.name}</Typography>
                                                <Typography variant="body2">{toFiniteNumber(row.views)}</Typography>
                                            </Box>
                                            <Box sx={{ height: 7, bgcolor: "#eef2f7", borderRadius: 99, overflow: "hidden", mt: 0.5 }}>
                                                <Box sx={{ height: "100%", width: `${pct}%`, bgcolor: "#f0ac4d" }} />
                                            </Box>
                                        </Box>
                                    );
                                })}
                            </Box>
                        ) : (
                            <Typography variant="body2" color="text.secondary">Keine Klickdaten.</Typography>
                        )}
                    </Paper>
                </Box>

                    </Box>
                </Box>

                <Box sx={{ pt: 1 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2 }}>
                        <Typography variant="h6" sx={{ fontWeight: 800 }}>
                            Matomo Webanalyse
                        </Typography>
                        <Button
                            variant="outlined"
                            onClick={fetchMatomoOnce}
                            disabled={matomoLoading}
                        >
                            Matomo neu laden
                        </Button>
                    </Box>

                    <Grid container spacing={3} sx={{ mt: 0 }}>
                <Grid item xs={12} md={4}>
                    <Paper sx={{ p: 3 }}>
                        <Typography variant="subtitle2" color="text.secondary">
                            Matomo – letzte Besucher
                        </Typography>

                        {matomoLoading ? (
                            <Box sx={{ mt: 1 }}>
                                <CircularProgress size={24} />
                            </Box>
                        ) : (
                            <Box sx={{ mt: 1 }}>
                                {lastVisits.length ? (
                                    lastVisits.map((v) => (
                                        <Box key={v.idVisit || `${v.visitIp}-${v.serverTimePretty}`} sx={{ py: 0.5 }}>
                                            <Typography variant="body2">
                                                {v?.visitIp || "?"} · {v?.country || ""} {v?.region || ""} ·{" "}
                                                {v?.serverTimePretty || ""}
                                            </Typography>
                                        </Box>
                                    ))
                                ) : (
                                    <Typography variant="body2" color="text.secondary">
                                        Keine Daten.
                                    </Typography>
                                )}
                            </Box>
                        )}
                    </Paper>
                </Grid>

                <Grid item xs={12} md={8}>
                    <Paper sx={{ p: 3 }}>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                            Matomo – Besuche (letzte 30 Tage)
                        </Typography>

                        {matomoLoading ? (
                            <CircularProgress size={24} />
                        ) : safeSeries.length ? (
                            <>
                                <Sparkline points={safeSeries} />
                                <Divider sx={{ my: 1.5 }} />
                                <Typography variant="body2" color="text.secondary">
                                    Min: {Math.min(...safeSeries)} · Max: {Math.max(...safeSeries)} · Letzter Wert:{" "}
                                    {safeSeries[safeSeries.length - 1]}
                                </Typography>
                            </>
                        ) : (
                            <Typography variant="body2" color="text.secondary">
                                Keine Daten.
                            </Typography>
                        )}
                    </Paper>
                </Grid>

                {showDebug && (
                    <Grid item xs={12}>
                        <Paper sx={{ p: 3 }}>
                            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                                Debug (Matomo Response – gekürzt)
                            </Typography>
                            <Typography variant="body2" sx={{ fontFamily: "monospace", whiteSpace: "pre-wrap" }}>
                                last-visits: {JSON.stringify(rawLast, null, 2)?.slice(0, 1200)}
                                {"\n\n"}
                                visits-series: {JSON.stringify(rawSeries, null, 2)?.slice(0, 1200)}
                            </Typography>
                        </Paper>
                    </Grid>
                )}
                    </Grid>
                </Box>
            </Box>
        </PageContainer>
    );
}

